import React, { useState } from "react";
import { addMonths, format } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import DateRangePresets from "./DateRangePresets";
import { cn } from "../../lib/utils";
import { UPCOMING_MONTHS } from "../../utils/constants/listFilters";

const fmt = (d) => format(d, "dd MMM yyyy");

/**
 * A single date-dimension filter: a labeled trigger button (e.g. "Requested Date")
 * that opens a popover with a from–to range calendar and quick-date preset chips.
 * The button shows the selected range so the active dimension is always unambiguous.
 *
 * `from`/`to` are Date | null; `onChange(from, to)` fires on any pick (live-applied).
 * The clear "✕" is a sibling of the trigger (not nested inside it) so its click is
 * independent of the popover's open/close handling.
 */
export default function DateRangeFilter({ label, from, to, onChange, className, upcoming = false }) {
  const [open, setOpen] = useState(false);
  const hasRange = !!(from || to);
  const summary = from && to ? `${fmt(from)} – ${fmt(to)}` : from ? `From ${fmt(from)}` : `Until ${fmt(to)}`;

  return (
    <div className={cn("relative inline-flex", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "h-9 justify-start gap-2 font-normal",
              hasRange ? "pr-8 text-foreground border-primary/40" : "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">{hasRange ? `${label}: ${summary}` : label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={{ from: from ?? undefined, to: to ?? undefined }}
            onSelect={(range) => onChange(range?.from ?? null, range?.to ?? null)}
            defaultMonth={from ?? undefined}
            captionLayout="dropdown"
            startMonth={new Date(2023, 0)}
            endMonth={upcoming ? addMonths(new Date(), UPCOMING_MONTHS) : new Date()}
          />
          <div className="mt-2 border-t pt-2">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Quick ranges</p>
            <DateRangePresets from={from} to={to} onSelect={(f, t) => onChange(f, t)} upcoming={upcoming} />
          </div>
          <div className="mt-2 flex justify-end border-t pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-gray-500 hover:text-gray-700"
              onClick={() => onChange(null, null)}
            >
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {hasRange && (
        <button
          type="button"
          aria-label={`Clear ${label}`}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          onClick={() => onChange(null, null)}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
